import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    getDensityUnit,
    getVolumeUnit,
    getWeightUnit,
} from '../../../constants'
import { getProductBySystemName } from '../../../services/manageBookingService'
import './SLRCargoDetails.scss'
import checkCircle from '../../../assets/images/check-circle-filled.svg'
import crossCircle from '../../../assets/images/close-circle-filled.svg'
import crossCircleRed from '../../../assets/images/close-circle-filled-red.svg'
import {
    clearItemFromSessionStorage, getBase64Decoded, getFromSessionStorage, setToSessionStorage, getBase64Encoded
} from '../../../utils/StorageUtils'
import WeightDetails from '../../ManageBooking/ViewBooking/WeightDetails'
import { useSelector } from "react-redux";

const SLRCargoDetails = ({ slrResponse }) => {
    //for multilingual
    const [t] = useTranslation(['labels', 'errors'])

    const [cargoDetails, setCargoDetails] = useState([])
    const [productName, setProductName] = useState('')
    const dgCheckNormal = getFromSessionStorage('ifDg')

    const masterData = useSelector(state => state?.masterData);

    useEffect(() => {
        // Map SLR cargoDetails directly
        if (slrResponse && slrResponse.cargoDetails) {
            setCargoDetails(slrResponse.cargoDetails)
        }
        // Fetch product description if available
        ; (async () => {
            try {
                let product = await getProductBySystemName(
                    slrResponse?.cargoDetails?.product
                )
                if (product) {
                    setProductName(product.description)
                }
            } catch (error) {
                setProductName(slrResponse?.cargoDetails?.product)
                console.log(error.message)
            }
        })()
    }, [slrResponse])

    // Prepare bookingDetails object for WeightDetails component
    const prepareBookingDetailsForWeightDetails = () => {
        return {
            weightUnit: slrResponse?.cargoDetails?.weightUnit || 'K',
            volumeUnit: slrResponse?.cargoDetails?.volumeUnit || 'C',
            dimensionUnit: slrResponse?.dimensionDetails?.[0]?.dimensionUnit || 'C',
            bookingCargoDetails: slrResponse?.cargoDetails || {},
            // Map dimensionDetails to weightDetails structure for WeightDetails component
            weightDetails: {
                looseDetails: (slrResponse?.dimensionDetails || []).map((dim) => ({
                    numberOfPieces: dim.pieces || 0,
                    length: dim.length || 0,
                    width: dim.width || 0,
                    height: dim.height || 0,
                    weight: dim.weightPerPiece && dim.pieces ? (dim.weightPerPiece * dim.pieces) : 0,
                    weightPerPiece: dim.weightPerPiece || 0,
                    stackable: dim.stackable || false,
                    tiltable: dim.tiltable || false,
                    craneLiftable: dim.craneLiftable || false,
                    forkLiftable: dim.forkLiftable || false,
                    fragile: dim.fragile || false,
                    packingType: dim.packingType || null,
                })),
                uldInfoDetaills: slrResponse?.uldDetails || [],
            },
            dangerousGoodsInformation: slrResponse?.dangerousGoodsInformation || [],
            additionalCommodityDetails: slrResponse?.additionalCommodityDetails || {},
        }
    }

    const bookingDetailsForWeightDetails = prepareBookingDetailsForWeightDetails()

    return (
        <div id='cargo-details' className='cargo-details-wrapper mt-1'>
            <div className='cargo-details-card-custom'>
                <div className='cargo-details-row responsive-cargo-row'>
                    {/* First row: Product, Commodity, SCC, Gross Weight */}
                    <div className='cargo-details-col'>
                        <div className='cargo-details-heading'>{t('COMMON.PRD')}</div>
                        <div className='cargo-details-data'>
                            {(() => {
                                const systemProductName = slrResponse?.cargoDetails?.product;
                                if (systemProductName && Array.isArray(masterData?.productsMaster)) {
                                    const found = masterData.productsMaster.find(
                                        prod => prod.systemProductName === systemProductName
                                    );
                                    return found ? found.description : systemProductName;
                                }
                                return '--';
                            })()}
                        </div>
                    </div>

                    <div className='cargo-details-col'>
                        <div className='cargo-details-heading'>{t('COMMON.COMMODITY')}</div>
                        <div className='cargo-details-data'>
                            {cargoDetails.commodity ? cargoDetails.commodity : '--'}
                        </div>
                    </div>

                    <div className='cargo-details-col'>
                        <div className='cargo-details-heading'>{t('COMMON.SCC')}</div>
                        <div className='cargo-details-data'>
                            {cargoDetails.sccCodes && cargoDetails.sccCodes.length > 0
                                ? cargoDetails.sccCodes.join(", ")
                                : '--'}
                        </div>
                    </div>

                    <div className='cargo-details-col'>
                        <div className='cargo-details-heading'>{t('COMMON.GROSS_WT')}</div>
                        <div className='cargo-details-data'>
                            {cargoDetails.grossWeight ? <>
                                {cargoDetails.grossWeight}
                                {getWeightUnit(cargoDetails?.weightUnit)}</> : '--'}
                        </div>
                    </div>
                </div>

                <div className='cargo-details-row responsive-cargo-row'>
                    {/* Second row: Volume, Density */}
                    <div className='cargo-details-col fixed-col-width'>
                        <div className='cargo-details-heading'>{t('COMMON.VOL')}</div>
                        <div className='cargo-details-data'>
                            {cargoDetails?.volume ? <>{cargoDetails.volume}
                                {getVolumeUnit(cargoDetails?.volumeUnit)}</> : '--'}
                        </div>
                    </div>

                    <div className='cargo-details-col fixed-col-width'>
                        <div className='cargo-details-heading'>{t('COMMON.DENSITY')}</div>
                        <div className='cargo-details-data'>
                            {cargoDetails.density
                                ? <>
                                    {cargoDetails.density}
                                    {getDensityUnit(cargoDetails?.weightUnit, cargoDetails?.volumeUnit)}
                                </>
                                : '--'}
                        </div>
                    </div>
                </div>

                {/* SLR-specific row: AWB and Shipment Description */}
                {(
                    cargoDetails?.awbNumber ||
                    cargoDetails?.shipmentDescription
                ) && (
                        <div className='cargo-details-row responsive-cargo-row'>
                            {cargoDetails?.awbNumber && (
                                <div className='cargo-details-col fixed-col-width'>
                                    <div className='cargo-details-heading'>{t('COMMON.AWB_NUMBER')}</div>
                                    <div className='cargo-details-data'>
                                        {cargoDetails.awbNumber}
                                    </div>
                                </div>
                            )}
                            {cargoDetails?.shipmentDescription && (
                                <div className='cargo-details-col fixed-col-width slr-booking-remarks-col'>
                                    <div className='cargo-details-heading'>{t('COMMON.SHIP_DES')}</div>
                                    <div className='cargo-details-data'>
                                        {cargoDetails.shipmentDescription}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                {(
                    cargoDetails?.product !== "LiveAnimals" &&
                    Array.isArray(slrResponse?.dangerousGoodsInformation) && slrResponse.dangerousGoodsInformation.length > 0 ||
                    cargoDetails?.product !== "LiveAnimals" && dgCheckNormal === 'false' &&
                    Array.isArray(slrResponse?.dangerousGoodsInformation) && slrResponse.dangerousGoodsInformation.length > 0) && (
                        <div className='cargo-details-row'>
                            <div className='cargo-details-col'>
                                <div className='cargo-details-heading'>{t('COMMON.UNIDSUMMARY')}</div>
                                <div className='cargo-details-data'>
                                    {slrResponse && slrResponse?.additionalCommodityDetails && Array.isArray(slrResponse.additionalCommodityDetails.dangerousGoods)
                                        ? slrResponse.additionalCommodityDetails.dangerousGoods.map((e, index) =>
                                            slrResponse.additionalCommodityDetails.dangerousGoods.length - 1 === index ? `${e.unid}` : `${e.unid}, `
                                        )
                                        : '--'}
                                </div>
                            </div>
                        </div>
                    )}

                {/* WeightDetails section for dimensions */}
                <WeightDetails bookingDetails={bookingDetailsForWeightDetails} isSlrBooking={true} />
            </div>
        </div>
    )
}

export default SLRCargoDetails
